import React, { useState, useEffect } from 'react'
import {
  makeStyles,
  AppBar,
  Tabs,
  Tab,
  Card,
  Divider,
  Snackbar,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslate, useRedirect } from 'react-admin'
import TabPanel from '../../../components/TabPanel/TabPanel'
import subscriptionPageStyles from './subscriptionPage.styles'
import MainSubscriptionTab from '../MainSubscriptionTab/MainSubscriptionTab'
import SubscriptionStatusTab from '../SubscriptionStatusTab/SubscriptionStatusTab'
import PaymentsTab from '../PaymentsTab/PaymentsTab'
import FaqTab from '../FaqTab/FaqTab'
import QueryKeys from '../../../utils/constants/queryKeys'
import COLORS from '../../../utils/constants/colors'
import LoaderDots from '../../../components/loaders/LoaderDots/LoaderDots'
import ContainerPadding from '../../../components/containerPadding/containerPadding'
import useUrlSearchParams from '../../../hooks/useUrlSearchParams'
import verifyTransaction from '../../../Apis/subscriptions/verifyTransaction'
import localStorageIds from '../../../utils/constants/localStorageIds'
import { setLocalStorageValue } from '../../../utils/helpers/localStorageHelperMethods'
import useHistoryPush from '../../../hooks/useHistoryPush'
import SubscriptionForm from '../../../components/form/forms/SubscriptionForm/SubscriptionForm'
import getSubscriptionDetails from '../../../Apis/subscriptions/getSubscriptionDetails'
import activateSubscription from '../../../Apis/subscriptions/activateSubscription'
import ConnectionErrorPage from '../../ErrorInConnection/ConnectionErrorPage'
import getOrgSubscriptionStatus from '../../../Apis/subscriptions/getOrgSubscriptionStatus'
import SUBSCRIPTION_STATES from '../../../utils/constants/subscriptionStates'

const SubscriptionPage = () => {
  const [tab, setTab] = useState(0)
  const [selectedMultipleAddons, setSelectedMultipleAddons] = useState([])
  const [paymentSuccess, setPaymentSuccess] = useState()
  const [isCardUpdateSnackbarOpen, setIsCardUpdateSnackbarOpen] = useState(false)
  const [cardSavedWhilePending, setCardSavedWhilePending] = useState(false)
  const queryClient = useQueryClient()
  const translate = useTranslate()
  const externalRedirect = useRedirect()
  const { org: orgId, t, tab: requestedTab } = useUrlSearchParams(['org', 't', 'tab']) || {}
  const { redirect, pathname } = useHistoryPush()

  // ?tab= deep link. An effect, not the useState initialiser: clicking a notice while already on
  // this page changes the URL without remounting.
  useEffect(() => {
    const wanted = Number(requestedTab)
    if (Number.isInteger(wanted) && wanted >= 0 && wanted <= 3) setTab(wanted)
  }, [requestedTab])
  const useStyles = makeStyles(subscriptionPageStyles)
  const classes = useStyles()


  const {
    data: subscriptionDetails,
    isLoading,
    error,
  } = useQuery([QueryKeys.GET_SUBSCRIPTION_DETAILS, orgId], () => getSubscriptionDetails({ orgId }), {
    enabled: Boolean(orgId),
  })

  useEffect(() => {
    if (subscriptionDetails && subscriptionDetails.addons) {
      const enabledAddons = subscriptionDetails.addons.filter((addon) => addon.enabled)
      setSelectedMultipleAddons(enabledAddons)
    }
  }, [subscriptionDetails])

  const activateOrgSubscription = useMutation((data) => activateSubscription(data))
  const orgSubStatusMutation = useMutation((data) => getOrgSubscriptionStatus(data), {
    onSuccess: (res) => {
      queryClient.setQueryData([QueryKeys.GET_ORG_SUB_STATUS, orgId], res)
      setLocalStorageValue([{ id: localStorageIds.SELECTED_ORG_SUB_STATUS, value: res }])
    },
  })

  const verifyTheTransaction = useQuery(
    [QueryKeys.VIVA_WEBHOOK, orgId, t],
    () =>
      verifyTransaction({
        orgId,
        vivaPaymentOrderInitialTransactionId: t,
      }),
    {
      enabled: Boolean(t && orgId),
      onSuccess: async (res) => {
        const { jwtToken, isTransactionSuccessful, isCardVerification } = res?.data || {}
        setLocalStorageValue([{ id: localStorageIds.TOKEN, value: jwtToken }])
        // Read after verifyTransaction has settled the payment, so it is the post-payment status.
        // Seed the cache with it too, or useOrgSubscriptionStatus serves the pre-payment status
        // for up to 60s.
        const status = await getOrgSubscriptionStatus({ orgId }).catch(() => undefined)
        if (status !== undefined) {
          setLocalStorageValue([{ id: localStorageIds.SELECTED_ORG_SUB_STATUS, value: status }])
          queryClient.setQueryData([QueryKeys.GET_ORG_SUB_STATUS, orgId], status)
        }

        if (isCardVerification) {
          // A pending org still owes its first payment: land on the overview tab, where the
          // Subscribe button lives, instead of stranding the owner on billing details.
          const stillPending = status === SUBSCRIPTION_STATES.PENDING
          setCardSavedWhilePending(stillPending)
          setTab(stillPending ? 0 : 1)
          setIsCardUpdateSnackbarOpen(true)
        } else {
          setPaymentSuccess(isTransactionSuccessful)
        }

        // The backend finalizes the payment inside verifyTransaction, so the
        // details query that raced this request at mount holds pre-payment
        // data — refetch it for both the payment and card-verification paths.
        queryClient.invalidateQueries(QueryKeys.GET_SUBSCRIPTION_DETAILS)

        // This is only to remove unnecessary url query params
        redirect(pathname, null, ['t', 's', 'lang', 'eventId', 'eci'])
      },
    },
  )

  const handleChange = (event, newValue) => {
    setTab(newValue)
  }

  const a11yProps = (index) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
      className: classes.indicator,
    }
  }

  const handleActivateSubscription = () => {
    const data = {
      orgId,
    }
    activateOrgSubscription.mutate(data, {
      onSuccess: (res) => {
        const { vivaPaymentOrderCode } = res?.data || {}
        if (vivaPaymentOrderCode) {
          externalRedirect(`${process.env.REACT_APP_VIVA_URL}${vivaPaymentOrderCode}`)
        } else {
          orgSubStatusMutation.mutate({ orgId })
          queryClient.invalidateQueries(QueryKeys.GET_SUBSCRIPTION_DETAILS).then()
          queryClient.invalidateQueries(QueryKeys.GET_ACCOUNT_ME).then()
          setTab(0)
          setPaymentSuccess(true)
        }
      },
    })
  }

  return (
    <Card className={classes.root}>
      <AppBar position='static' color='transparent' elevation={0} className={classes.appbar}>
        <Tabs
          value={tab}
          onChange={handleChange}
          variant='scrollable'
          scrollButtons='auto'
          TabIndicatorProps={{ style: { background: COLORS.subscriptionBlue } }}
          textColor='inherit'
        >
          <Tab
            label={translate('ra.page.subscription')}
            {...a11yProps(0)}
            style={{
              color: tab === 0 ? COLORS.subscriptionBlue : COLORS.lightGrey,
              minWidth: 'auto', padding: '6px 20px',
            }}
            disabled={!!error}
          />
          <Tab
            label={translate('ra.page.billingDetails')}
            {...a11yProps(1)}
            style={{
              color: tab === 1 ? COLORS.subscriptionBlue : COLORS.lightGrey,
              minWidth: 'auto', padding: '6px 20px',
            }}
            disabled={!!error}
          />
          <Tab
            label={translate('ra.page.payments')}
            {...a11yProps(2)}
            style={{
              color: tab === 2 ? COLORS.subscriptionBlue : COLORS.lightGrey,
              minWidth: 'auto', padding: '6px 20px',
            }}
            disabled={!!error}
          />
          <Tab
            label='FAQ'
            {...a11yProps(3)}
            style={{
              color: tab === 3 ? COLORS.subscriptionBlue : COLORS.lightGrey,
              minWidth: 'auto', padding: '6px 20px',
            }}
          />
        </Tabs>
      </AppBar>
      <Divider light orientation='horizontal' />
      <TabPanel value={tab} index={0}>
        {verifyTheTransaction.isLoading || isLoading ? (
          <ContainerPadding paddingValue='45%'>
            <LoaderDots
              style={{
                fill: COLORS.orange,
                width: 70,
                height: 70,
              }}
            />
          </ContainerPadding>
        ) : paymentSuccess !== undefined ? (
          <SubscriptionStatusTab
            selectedMultipleAddons={selectedMultipleAddons}
            paymentSuccess={paymentSuccess}
            setPaymentSuccess={setPaymentSuccess}
            isCardVerification={verifyTheTransaction?.data?.data?.isCardVerification || true}
          />
        ) : !error ? (
          <MainSubscriptionTab
            setPaymentSuccess={setPaymentSuccess}
            subscriptionDetails={subscriptionDetails}
            orgId={orgId}
            handleActivateSubscription={handleActivateSubscription}
            isSubActivationLoading={activateOrgSubscription.isLoading}
            setTab={setTab}
          />
        ) : (
          <ConnectionErrorPage />
        )}
      </TabPanel>
      <TabPanel value={tab} index={1} disabled={!!error}>
        <div style={{ width: '60%', margin: 'auto' }}>
          <SubscriptionForm
            setPaymentSuccess={setPaymentSuccess}
            showCard
            setTab={setTab}
            handleActivateSubscription={handleActivateSubscription}
            isSubActivationLoading={activateOrgSubscription.isLoading}
          />
        </div>
      </TabPanel>
      <TabPanel value={tab} index={2} disabled={true}>
        <PaymentsTab />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <FaqTab />
      </TabPanel>

      <Snackbar
        open={isCardUpdateSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setIsCardUpdateSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setIsCardUpdateSnackbarOpen(false)}
          severity='success'
          variant='filled'
        >
          {cardSavedWhilePending
            ? translate('ra.text.cardSavedSubscribe')
            : 'Your card has been added or updated'}
        </Alert>
      </Snackbar>
    </Card>
  )
}

export default SubscriptionPage
